import { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import config from "../config/config";
import FormControl from "./Common/FormControl";
import FormLabel from "./Common/FormLabel";
import ToastNotification from "./Common/ToastNotification";
import { CONSTANTS } from "../utils/Constant/Constant";
import { useNavigate } from "react-router-dom";

const resetPasswordConfig = {
  authority: `${config.MSAL_FORGOT_PASSWORD}`,
  clientId: config.MSAL_CLIENT_ID,
  redirectUri: config.MSAL_REDIRECT_URI,
  scopes: ["openid", "profile", "email", "offline_access"],
  knownAuthorities: [`${config.MSAL_AUTHORITY_AUTHORITIES}`],
};

const Login = ({ login }) => {
  const navigate = useNavigate();
  const { instance } = useMsal();

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    username: "",
    password: "",
    global: "",
  });

  // Toast state
  const [toastData, setToastData] = useState(null);
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "", // clear field-specific error
      global: "", // clear global error
    }));
  };

  // Azure B2C login (ROPC flow)
  const loginWithB2C = async (username, password) => {
    const clientId = config.MSAL_CLIENT_ID;
    const scope = config.SCOPE_USER_READWRITE_ALL;
    const policy = config.CUSTOME_POLICY_NAME;
    const tenant = config.CUSTOME_TENANT_NAME;

    const b2cloginURL = `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/${policy}/oauth2/v2.0/token`;

    const body = new URLSearchParams({
      grant_type: "password",
      username,
      password,
      scope: `${scope} openid offline_access`,
      client_id: clientId,
    }).toString();

    const response = await fetch(b2cloginURL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error_description || "Invalid credentials");
    }

    if (!data.access_token) {
      throw new Error("No access token received");
    }

    return data;
  };

  // Handle form submit
  const handleLogin = async () => {
    setFormErrors({ username: "", password: "", global: "" });

    // Field-level validation
    let errors = {};
    if (!formData.username) {
      errors.username = "Email is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.username)) {
        errors.username = "Please enter a valid email address.";
      }
    }

    if (!formData.password) {
      errors.password = "Password is required.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors((prev) => ({ ...prev, ...errors }));
      return;
    }

    setLoading(true);
    try {
      const data = await loginWithB2C(formData.username, formData.password);

      //  Save tokens / redirect user
      localStorage.setItem("Token", data.access_token);
      localStorage.setItem("RefreshToken", data.refresh_token || "");
      localStorage.setItem("userName", formData.username);
      login();
      navigate("/dashboard");
    } catch (err) {
      setToastData({
        type: CONSTANTS.TOAST_ERROR,
        heading: "Login Failed",
        message: "Login failed. Please check your credentials and try again",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleForgotPassword = async () => {
    try {
      const result = await instance.loginPopup({
        scopes: resetPasswordConfig.scopes,
        authority: resetPasswordConfig.authority,
        redirectUri: resetPasswordConfig.redirectUri,
      });

      if (result.idToken) {
        setFormErrors({ username: "", password: "", global: "" });

        setToastData({
          type: CONSTANTS.TOAST_SUCCESS,
          heading: "Password Reset",
          message:
            "Password reset successful. Please log in with your new password.",
        });
      }
    } catch (err) {
      console.error("Password reset error:", err);

      if (err.errorMessage && err.errorMessage.includes("AADB2C90118")) {
        console.warn("Password reset cancelled by user.");
        setToastData({
          type: CONSTANTS.TOAST_WARNING,
          heading: "Password Reset",
          message: "Password reset cancelled.",
        });
      } else {
        setToastData({
          type: CONSTANTS.TOAST_ERROR,
          heading: "Password Reset",
          message: "Password reset failed. Try again.",
        });
      }
    }
  };

  return (
    <div className="d-flex">
      <div className="login-image-background" />
      <div className="d-flex flex-column justify-content-center align-items-center login-details-background p-4">
        <div className="d-flex justify-content-center flex-column p-5">
          <h2>Sign In</h2>
          <p className="fs-3">Please sign in to ROAR<sup>AI</sup> platform</p>

          {/* Username Field */}
          <div className="mb-3">
            <FormLabel label="Email" required />
            <FormControl
              type="text"
              placeholder="Email"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              height="45"
              borderRadius="5"
              autoComplete="off"
              isInvalid={!!formErrors.username}
              feedback={formErrors.username}
            />
          </div>

          {/* Password Field */}
          <div className="mb-3">
            <FormLabel label="Password" required />
            <div className="position-relative">
              <FormControl
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                height="45"
                borderRadius="5"
                autoComplete="off"
                isInvalid={!!formErrors.password}
                feedback={formErrors.password}
              />
              <i
                className={`bi ${
                  showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                }`}
                onClick={() => setShowPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  fontSize: "1rem",
                  color: "#6c757d",
                }}
              />
            </div>

            {/* Forgot Password Link */}
            <div className="d-flex justify-content-end mt-2 mb-2">
              <a
                href="/forgot-password"
                className="forgot-password-label"
                onClick={(e) => {
                  e.preventDefault();
                  handleForgotPassword();
                }}
              >
                Forgot password
              </a>
            </div>
          </div>

          {/* Toast Notification */}
          {toastData && (
            <ToastNotification
              type={toastData.type}
              heading={toastData.heading}
              message={toastData.message}
            />
          )}
          <Button
            variant="primary"
            className="loginButton create-button"
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Signing in…
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Login;
